import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { J1DownarrowD, J2Multiply, J2Validate } from '@e3dc-react/icons'
import { LabeledOption } from 'src/Types/globalTypes'

type Props = {
  name: string
  options: Array<LabeledOption<string> & { disabled?: boolean }>
  onChange?: (value: string[]) => void
  onNativeChange?: (event: SelectChangeEvent<string[]>) => void
  clearAllLabel?: string
  selectAllLabel?: string
  helperText?: string
} & Omit<SelectProps<string[]>, 'name' | 'multiple' | 'onChange'>

/**
 * MUI MultiSelect with react-hook-form
 */
const BaseMultiSelect: React.FC<Props> = ({
  options,
  label,
  fullWidth,
  name,
  value,
  onChange,
  onNativeChange,
  renderValue,
  clearAllLabel,
  selectAllLabel,
  helperText,
  error,
  ...props
}) => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const applyFullWidth = fullWidth || downSm
  const selectedValues = Array.isArray(value) ? value : []
  const muiTheme = useTheme()

  const onClearAll = (): void => {
    onChange?.([])
  }

  const onSelectAll = (): void => {
    onChange?.(options.map(option => option.value))
  }

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const selected = event.target.value
    const nextValues = typeof selected === 'string' ? selected.split(',') : selected

    onChange?.(nextValues)
    onNativeChange?.(event)
  }

  return (
    <FormControl fullWidth={applyFullWidth}>
      {label && <InputLabel>{label}</InputLabel>}
      <MuiSelect
        sx={{
          '& .MuiSelect-select': {
            paddingBlock: 1.5,
            paddingInline: 2,
          },
        }}
        label={label}
        IconComponent={J1DownarrowD}
        fullWidth={applyFullWidth}
        name={name}
        multiple
        value={selectedValues}
        onChange={handleChange}
        renderValue={
          renderValue ??
          (selected => {
            const values = Array.isArray(selected) ? selected : [selected]
            return values.map(item => options.find(option => String(option.value) === String(item))?.label ?? String(item)).join(', ')
          })
        }
        {...props}
      >
        {options.map(option => {
          const isSelected = selectedValues.includes(String(option.value))

          return (
            <MenuItem key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
              <Checkbox checked={isSelected} /> {option.label}
            </MenuItem>
          )
        })}
        {selectAllLabel && (
          <Box sx={{ borderTop: `1px solid ${muiTheme.palette.divider}`, px: 2, py: 1 }}>
            <Button variant="text" color="inherit" onClick={onSelectAll} fullWidth startIcon={<J2Validate />}>
              {selectAllLabel}
            </Button>
          </Box>
        )}
        {clearAllLabel && (
          <Box sx={{ borderTop: `1px solid ${muiTheme.palette.divider}`, px: 2, py: 1 }}>
            <Button variant="text" color="inherit" onClick={onClearAll} fullWidth startIcon={<J2Multiply />}>
              {clearAllLabel}
            </Button>
          </Box>
        )}
      </MuiSelect>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  )
}

BaseMultiSelect.displayName = 'BaseMultiSelect'

export default BaseMultiSelect
